import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import {
  directoryApi,
  type CreateSubcontractorTrailerRequest,
  type SubcontractorTrailer,
  type UpdateSubcontractorTrailerRequest,
} from '../client';

/** Stable query-key factory for subcontractor-trailer caches. */
export const subcontractorTrailerKeys = {
  all: ['directory', 'subcontractorTrailer'] as const,
  byOrganisation: (organisationId: string) =>
    ['directory', 'subcontractorTrailer', 'organisation', organisationId] as const,
};

/**
 * Trailers subcontractors have left with us, for the given organisation. Pass
 * `null` to keep the query disabled while the org id resolves.
 */
export function useSubcontractorTrailers(
  organisationId: string | null,
  options?: Omit<UseQueryOptions<SubcontractorTrailer[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<SubcontractorTrailer[], Error>({
    queryKey: organisationId
      ? subcontractorTrailerKeys.byOrganisation(organisationId)
      : subcontractorTrailerKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return directoryApi.getSubcontractorTrailers(organisationId);
    },
    enabled: !!organisationId,
    staleTime: 60 * 1000,
    ...options,
  });
}

export function useCreateSubcontractorTrailer() {
  const qc = useQueryClient();
  return useMutation<string, Error, CreateSubcontractorTrailerRequest>({
    mutationKey: [...subcontractorTrailerKeys.all, 'create'],
    mutationFn: (body) => directoryApi.createSubcontractorTrailer(body),
    onSuccess: (_id, vars) => {
      qc.invalidateQueries({
        queryKey: subcontractorTrailerKeys.byOrganisation(vars.organisationId),
      });
    },
  });
}

/**
 * PUT a trailer. `organisationId` rides alongside the body so the list cache can
 * be invalidated without a second round-trip — the wire body carries only the
 * trailer id.
 */
export function useUpdateSubcontractorTrailer() {
  const qc = useQueryClient();
  return useMutation<
    string,
    Error,
    { organisationId: string; body: UpdateSubcontractorTrailerRequest }
  >({
    mutationKey: [...subcontractorTrailerKeys.all, 'update'],
    mutationFn: ({ body }) => directoryApi.updateSubcontractorTrailer(body),
    onSuccess: (_id, { organisationId }) => {
      qc.invalidateQueries({ queryKey: subcontractorTrailerKeys.byOrganisation(organisationId) });
    },
  });
}

export function useDeleteSubcontractorTrailer() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { organisationId: string; trailerId: string }>({
    mutationKey: [...subcontractorTrailerKeys.all, 'delete'],
    mutationFn: ({ trailerId }) => directoryApi.deleteSubcontractorTrailer(trailerId),
    onSuccess: (_res, { organisationId }) => {
      qc.invalidateQueries({ queryKey: subcontractorTrailerKeys.byOrganisation(organisationId) });
    },
  });
}
